import { lazy, Suspense } from 'react'

export type {
  InitiativeRegistry,
  InitiativeScenarios,
  Scenario,
  ScenarioHandler,
  ScenarioHandlers,
  ScenarioRequest,
  ScenarioResponse
} from './runtime'

// Everything the apps may import up front. The runtime (`/runtime`) and the dock load lazily behind the gate.

const flag = import.meta.env.VITE_DESIGN_QA_SCENARIOS

// Production builds (release and PR previews alike) opt in explicitly; the release env leaves it unset so the
// guarded dynamic imports are dead code and the scenario chunks are never emitted.
export const DESIGN_QA_SCENARIOS_ENABLED: boolean = import.meta.env.DEV ? flag !== 'false' : flag === 'true'

type DesignQaRole = 'implementation' | 'prototype'

/** Prototype branches set `VITE_DESIGN_QA_ROLE=prototype`; everything else is the implementation. */
export const DESIGN_QA_ROLE: DesignQaRole =
  import.meta.env.VITE_DESIGN_QA_ROLE === 'prototype' ? 'prototype' : 'implementation'

export const SCENARIO_PARAM = 'scenario'
export const SCENARIO_KEY = 'designQaScenario'

/** Whether a scenario may be active this page load, read before loading any scenario code. */
export function mayHaveDesignQaScenario(search: string): boolean {
  if (new URLSearchParams(search).has(SCENARIO_PARAM)) return true
  try {
    return window.sessionStorage.getItem(SCENARIO_KEY) !== null
  } catch {
    return false
  }
}

let scenarioFlags: Record<string, boolean | string> | undefined

/** The active scenario's feature flags, merged into the app's overrides but never saved with them. */
export function getDesignQaScenarioFlags(): Record<string, boolean | string> | undefined {
  return scenarioFlags
}

export function setDesignQaScenarioFlags(flags: Record<string, boolean | string> | undefined): void {
  scenarioFlags = flags && Object.keys(flags).length ? flags : undefined
}

const ControlsDock = DESIGN_QA_SCENARIOS_ENABLED ? lazy(() => import('./ControlsDock')) : null

/** The bottom-right design QA dock; renders nothing when scenarios are off. */
export function DesignQaControls() {
  return ControlsDock ? (
    <Suspense fallback={null}>
      <ControlsDock />
    </Suspense>
  ) : null
}
